import './DTXAdhesionAd.scss';
import {
  CSSProperties,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { renderAd } from '@pages/Root/utils/dtx-ads.ts';
import { useSearchParams } from 'react-router';
import { isDntEnabled } from '@utils/dnt.ts';
import { DTX_ADHESION_AD_TAG } from '@moment/layouts/Ads/constants.ts';
import { BannerAdSizes } from '@moment/components/GalleryAd/ad-constants.ts';
import { useAds } from '@moment/context/AdsContext.tsx';
import { useTranslation } from 'react-i18next';

export interface DTXAdhesionAdProps {
  adUnitId: string;
  fallbackAd?: Element | ReactElement;
  style?: CSSProperties;
  iabCategories?: string[];
}

const getAdSizesForWidth = (width: number) => {
  if (width >= 1200) {
    return [
      [320, 50],
      [300, 50],
      [320, 100],
      [970, 90],
      [728, 90],
      [468, 60],
    ];
  } else if (width >= 992) {
    return [
      [320, 50],
      [300, 50],
      [320, 100],
      [728, 90],
      [970, 90],
      [468, 60],
    ];
  } else if (width >= 768) {
    return [
      [320, 50],
      [300, 50],
      [320, 100],
      [728, 90],
      [468, 60],
    ];
  } else {
    return [
      [320, 50],
      [300, 50],
      [320, 100],
    ];
  }
};

export const DTXAdhesionAd = (props: DTXAdhesionAdProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [dtxAdSizes, setDtxAdSizes] = useState<BannerAdSizes[]>([]);
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [adLoaded, setAdLoaded] = useState(false);
  const [displayAd, setDisplayAd] = useState(true);
  const [isDoNotSell] = useState<boolean>(isDntEnabled());

  const { isReady, adConfigData } = useAds();

  const pageParams = useMemo(() => {
    return {
      dtxTestResponseId: searchParams.get('dtx-test-response-id'),
      dtxTestDistributorId: searchParams.get('dtx-test-distributor-id'),
    };
  }, [searchParams]);
  const [showFallbackAd, setShowFallbackAd] = useState(false);

  const adSlotId = `dtx_ad_unit_${props.adUnitId}`;
  const adTestData =
    pageParams.dtxTestResponseId && pageParams.dtxTestDistributorId
      ? {
          mock_response_id: pageParams.dtxTestResponseId,
          distributor_id: Number(pageParams.dtxTestDistributorId),
        }
      : null;

  useEffect(() => {
    const displayAd = async () => {
      console.debug(
        `Rendering ad ${adSlotId}, sizes=${JSON.stringify(dtxAdSizes)}, iabCategories=${JSON.stringify(props.iabCategories)}, test_data=${JSON.stringify(adTestData)}`
      );
      // NOTE: iabCategories do not currently conform to DTX standard, omitting for now
      return renderAd(
        adSlotId,
        adConfigData?.adTags?.adTags?.['adh']?.placementID ||  DTX_ADHESION_AD_TAG,
        dtxAdSizes,
        lang,
        undefined,
        adTestData
      );
    };

    if (!adLoaded && dtxAdSizes.length > 0 && isReady) {
      if (!isDoNotSell) {
        displayAd().then((success) => {
          console.debug(`Ad ${adSlotId} rendered? ${success}`);
          setDisplayAd(success);
          // success events are handled in DTXAdsLayout
          if (!success) {
            if (props.fallbackAd) {
              console.debug(`Displaying fallback ad for ${adSlotId}`);
              setShowFallbackAd(true);
            }
          }
        });
      } else {
        setDisplayAd(false);
      }
      setAdLoaded(true);
    }
  }, [dtxAdSizes, adLoaded, isDoNotSell, isReady, props.iabCategories]);

  const adSizes = useMemo(() => {
    const dtxAdDimensions = getAdSizesForWidth(window.innerWidth);
    const bannerAds = dtxAdDimensions.map((i) => {
      return new BannerAdSizes(i[0], i[1]);
    });
    return bannerAds;
  }, [window.innerWidth]);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  useEffect(() => {
    setDtxAdSizes(adSizes);
  }, [dtxAdSizes]);

  return useMemo(() => {
    if (isDoNotSell) return null;
    return !showFallbackAd ? (
      displayAd ? (
        <div
          className="dtx-adhesion-ad--block"
          data-testid="dtx-ad-container"
          style={props.style || {}}
        >
          <button onClick={toggleVisibility}>
            <span id="mp-banner-arrow-icon">{isVisible ? '▼' : '▲'}</span>
          </button>
          <div
            key="ad"
            className={`dtx-adhesion__ad ${isVisible ? '' : 'dtx-adhesion__ad--hide'}`}
            id={`dtx_ad_unit_${props.adUnitId}`}
          ></div>
        </div>
      ) : null
    ) : (
      props.fallbackAd
    );
  }, [showFallbackAd, displayAd, isVisible, props.style, isReady]);
};
